'use strict';

import * as debounce from 'lodash/debounce';
import { CONST, documentReady, isAemMobileView, lockScroll, unlockScroll } from 'Util/util';
import 'Components/modals/VZModal';
import 'Components/minicart';

const classes = {
    navigationActive: 'navigation-active',
    open: 'open',
    scrollLock: 'scroll-lock',
    sticky: 'sticky',
    headerHidden: 'header-hidden'
};

const selectors = {
    body: 'body',
    flyout: '.flyout',
    header: 'header',
    pageHeader: '.js-page-header',
    navigation: '.js-navigation',
    navigationColumn: '.js-navigation-column',
    navigationItems: '.js-navigation-item',
    navigationTitles: '.js-navigation-column-title',
    navigationToggler: '.js-navigation-toggler',
    navigationWrapper: '.js-navigation-wrapper',
    navigationFlyoutButtons: '.js-navigation-item-button',
    notification: '.js-notification',
    notificationContent: '.js-notification .alert',
    subNavigation: '.js-sub-navigation',
    subNavigationWrapper: '.js-sub-navigation-wrapper',
    storefrontPage: '.pd-page',
    subNavigationBtnSmallBusiness: '.js-btn-small-business',
    subNavigationBtnLargeBusiness: '.js-btn-large-business'
};

const body = document.querySelector(selectors.body);
const notification = document.querySelector(selectors.notification);
let header;
let pageHeader;
let navigation;
let navigationToggler;
let navigationWrapperMobile;
let scrollTicking = false;
let lastScrollY = window.scrollY;
let resizeHandler = null;

/**
 * @private updateFlyoutAccessibilityState
 * @description
 * Keep hidden flyouts out of keyboard navigation.
 */
function updateFlyoutAccessibilityState() {
    if (!navigation || isAemMobileView()) return;

    navigation.querySelectorAll(selectors.navigationItems).forEach((navItem) => {
        const flyout = navItem.querySelector(selectors.flyout);
        if (!flyout) return;

        const isVisible = navItem.classList.contains('active');

        flyout.inert = !isVisible;
        flyout.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
    });
}
/**
 * @private handleScrollBehavior
 *
 * @description
 * - Scrolling down: hides the full header (red audience navigation + white navigation) by sliding it
 *   out of view.
 * - Scrolling up (while not at the very top): reveals only the white navigation, sticky at the top,
 *   keeping the red audience navigation collapsed.
 * - At the very top of the page: shows the full header (audience navigation + navigation) together.
 */
function handleScrollBehavior() {
    // Don't modify sticky/hidden classes when scroll is locked
    if (body.classList.contains(classes.scrollLock)) {
        scrollTicking = false;
        return;
    }

    const currentScrollY = window.scrollY;
    const scrollingDown = currentScrollY > lastScrollY;

    body.classList.toggle(classes.sticky, currentScrollY > 0);

    if (currentScrollY <= 0) {
        body.classList.remove(classes.headerHidden);
    } else {
        body.classList.toggle(classes.headerHidden, scrollingDown);
    }
    lastScrollY = currentScrollY;
    scrollTicking = false;
}

/**
 * @private onScroll
 * @description Throttles handleScrollBehavior to one call per animation frame for a smooth scroll.
 */
function onScroll() {
    if (!scrollTicking) {
        window.requestAnimationFrame(handleScrollBehavior);
        scrollTicking = true;
    }
}

/**
 * @private updateNotificationHeight
 *
 * @description
 * If there is a notification element:
 * - Adjust the padding on the body
 * - Set a height on the notification for css-transition purposes
 */
function updateNotificationHeight() {
    notification.setAttribute('style', 'height: auto !important');
    body.style.paddingTop = 'auto';

    const notificationHeight = notification.offsetHeight;
    const headerHeight = header.offsetHeight;

    notification.style.height = `${notificationHeight}px`;
    body.style.paddingTop = `${headerHeight}px`;
}

/**
 * @private handleDesktopClickClasses
 *
 * @description
 * Will toggle an active class on the navigation when clicking navigation items for desktop
 */
function handleDesktopClickClasses() {
    if (isAemMobileView()) return;

    const flyoutTriggers = document.querySelectorAll(selectors.navigationItems);

    flyoutTriggers.forEach((trigger) => {
        const button = trigger.querySelector(selectors.navigationFlyoutButtons);

        if (!button) return;

        button.addEventListener(CONST.events.click, (evt) => {
            evt.preventDefault();
            evt.stopPropagation();

            // Close all other flyouts
            flyoutTriggers.forEach((otherTrigger) => {
                if (otherTrigger !== trigger) {
                    otherTrigger.classList.remove('active');
                }
            });

            // Toggle current flyout
            trigger.classList.toggle('active');

            // Toggle navigation-open class and scroll lock on page header
            const hasActiveNavigation = document.querySelector(`${selectors.navigationItems}.active`);
            if (hasActiveNavigation) {
                pageHeader?.classList.add(classes.navigationActive);
                lockScroll();
            } else {
                pageHeader?.classList.remove(classes.navigationActive);
                unlockScroll();
            }

            updateFlyoutAccessibilityState();
        });
    });

    // Close flyouts when clicking outside
    document.addEventListener(CONST.events.click, (evt) => {
        // Only handle this for mobile view and when menu is open
        if (!pageHeader?.classList.contains(classes.navigationActive)) return;

        if (!evt.target.closest(selectors.navigation)) {
            flyoutTriggers.forEach((trigger) => {
                trigger.classList.remove('active');
            });
            // Remove navigation-open class and scroll lock when closing all flyouts
            pageHeader?.classList.remove(classes.navigationActive);
            unlockScroll();
            updateFlyoutAccessibilityState();
        }
    });
}

/**
 * @private bindItemToggling
 * @param {HTMLElement} button - The navigation button that can be toggled
 *
 * @description
 * Will create toggling functionality on navigation item buttons
 */
function bindItemToggling(button) {
    button.addEventListener(CONST.events.click, (evt) => {
        const targetElement = evt.target.closest(selectors.navigationItems);
        const allNavigationItems = document.querySelectorAll(selectors.navigationItems);

        // Close all other items
        allNavigationItems.forEach((item) => {
            if (item !== targetElement) {
                item.classList.remove(classes.open);
            }
        });

        // Toggle current item
        targetElement.classList.toggle(classes.open);
    });
}

/**
 * @private updateMobileMenuPosition
 *
 */
function updateMobileMenuPosition() {
    if (isAemMobileView() && navigationWrapperMobile) navigationWrapperMobile.style.top = `${header.offsetHeight}px`;
}

/**
 * @private toggleMobileMenu
 *
 * @description
 * Calculate top offset for navigation & handle scrollLock/active classes
 */
function toggleMobileMenu() {
    updateMobileMenuPosition();

    if (navigation.classList.contains(classes.navigationActive)) {
        navigation.classList.remove(classes.navigationActive);
        navigationToggler.classList.remove(classes.navigationActive);
        pageHeader?.classList.remove(classes.navigationActive);
        unlockScroll();
    } else {
        navigation.classList.add(classes.navigationActive);
        navigationToggler.classList.add(classes.navigationActive);
        pageHeader?.classList.add(classes.navigationActive);
        lockScroll();
    }
}

export default documentReady(() => {
    header = document.querySelector(selectors.header);
    pageHeader = document.querySelector(selectors.pageHeader);
    navigation = document.querySelector(selectors.navigation);
    const subNavigation = document.querySelector(selectors.subNavigation);
    const subNavigationBtnSmallBusiness = document.querySelector(selectors.subNavigationBtnSmallBusiness);
    const subNavigationBtnLargeBusiness = document.querySelector(selectors.subNavigationBtnLargeBusiness);

    if (!navigation) return;

    navigationToggler = navigation.querySelector(selectors.navigationToggler);
    navigationWrapperMobile = document.querySelector(selectors.navigationWrapper);

    // If there is a notification, adjust the padding on the body and set a height on the notification
    if (document.querySelector(selectors.notificationContent)) {
        updateNotificationHeight();

        resizeHandler = debounce(() => {
            updateNotificationHeight();
            updateMobileMenuPosition();
        }, 100);
        window.addEventListener('resize', resizeHandler);
    }

    // Handle scrolling & sticky header
    handleScrollBehavior();
    document.addEventListener('scroll', onScroll);

    // Handle classes for desktop
    handleDesktopClickClasses();

    // Handle classes & offset for mobile navigation
    if (!navigationToggler) return;
    navigationToggler.addEventListener(CONST.events.click, toggleMobileMenu);

    // Close mobile menu when clicking outside
    document.addEventListener(CONST.events.click, (evt) => {
        // Only handle this for mobile view and when menu is open
        if (!isAemMobileView() || !navigation.classList.contains(classes.navigationActive)) return;

        // Check if click is outside navigation
        if (!evt.target.closest(selectors.navigation)) {
            navigation.classList.remove(classes.navigationActive);
            navigationToggler.classList.remove(classes.navigationActive);
            pageHeader?.classList.remove(classes.navigationActive);
            unlockScroll();
        }
    });

    // For mobile, add a click event to toggle collapse
    if (isAemMobileView()) {
        [...navigation.querySelectorAll(selectors.navigationFlyoutButtons)].forEach(bindItemToggling);
    }

    if (!isAemMobileView()) updateFlyoutAccessibilityState();

    [subNavigationBtnSmallBusiness, subNavigationBtnLargeBusiness]
        .filter((item) => item)
        .forEach((subNavButton) => {
            subNavButton.addEventListener(CONST.events.click, (evt) => {
                const isEmptyBasket = subNavigation.dataset.basketIsEmpty;
                if (isEmptyBasket === 'false') {
                    evt.preventDefault();
                    const notEmptyBasketModal = document.getElementById('modal-basket-not-empty');
                    const primaryBtnText = subNavButton.dataset.modalBtnText;
                    notEmptyBasketModal.render({ primaryBtnText, primaryBtnUrl: evt.target.href });
                }
            });
        });
});

/**
 * @public destroy
 * @description Removes the scroll and resize listeners bound during init.
 * Call this if navigation.js's DOM (header/navigation) is ever torn down
 * without a full page reload.
 */
export function destroy() {
    document.removeEventListener('scroll', onScroll);

    if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler);
        resizeHandler = null;
    }
}
